import React, { useState, useRef, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useData } from "@/context/DataContext";
import { PageTitle } from "@/components/shared";
import { ANIMALS, ANIMAL_CATS, getAnimal } from "@/lib/animals";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Upload, Download, Share2, Camera, Trash2, Users2, Link2, Copy, Unlink, Crown, Clock, ExternalLink } from "lucide-react";
import { exportBackup } from "@/lib/backup";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/shared";

const PLAN_LABELS = { bulan1: "Paket 1 Bulan", bulan6: "Paket 6 Bulan", premium: "Paket Premium", none: "Belum Ada Paket" };

// Resize + compress an image file to a small base64 data URL
function fileToAvatar(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const size = 256;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { setPreview } = useTheme();
  const { data, activePasangan, pasanganInfo, reloadAll } = useData();
  const [animal, setAnimal] = useState(user?.animal || "");
  const [savingAnimal, setSavingAnimal] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [conf, setConf] = useState("");
  const [show, setShow] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [uploading, setUploading] = useState(false);
  const [namaPasangan, setNamaPasangan] = useState("");
  const [savingNama, setSavingNama] = useState(false);
  const [uname, setUname] = useState(user?.username || "");
  const [savingUname, setSavingUname] = useState(false);
  const [emailNew, setEmailNew] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailStep, setEmailStep] = useState("idle"); // idle | code
  const [emailBusy, setEmailBusy] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = user?.role === "sysadmin";
  const pid = isAdmin ? activePasangan : user?.pasangan_id;

  useEffect(() => {
    setNamaPasangan(pasanganInfo?.nama || "");
  }, [pasanganInfo?.nama]);

  useEffect(() => { setUname(user?.username || ""); }, [user?.username]);

  // Clear theme preview when leaving the page
  useEffect(() => () => setPreview(null), [setPreview]);

  const pickAnimal = (key) => {
    setAnimal(key);
    if (!isAdmin) setPreview(getAnimal(key).c); // live preview
  };

  const saveAnimal = async () => {
    setSavingAnimal(true);
    try {
      await api.put("/auth/profile", { animal });
      await refreshUser();
      setPreview(null);
      toast.success("Karakter hewan diperbarui — tema tersimpan!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingAnimal(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File harus berupa gambar"); return; }
    setUploading(true);
    try {
      const b64 = await fileToAvatar(file);
      setAvatar(b64);
      await api.put("/auth/profile", { avatar: b64 });
      await refreshUser();
      toast.success("Foto profil diperbarui");
    } catch {
      toast.error("Gagal mengunggah foto");
    } finally { setUploading(false); }
  };

  const removeAvatar = async () => {
    setUploading(true);
    try {
      await api.put("/auth/profile", { avatar: "" });
      setAvatar("");
      await refreshUser();
      toast.success("Foto profil dihapus — kembali ke karakter hewan");
    } catch {
      toast.error("Gagal menghapus foto");
    } finally { setUploading(false); }
  };

  const savePw = async () => {
    if (!cur || !nw) { toast.error("Isi password saat ini & password baru"); return; }
    if (nw !== conf) { toast.error("Konfirmasi password tidak cocok"); return; }
    setSavingPw(true);
    try {
      await api.put("/auth/profile", { current_password: cur, new_password: nw });
      toast.success("Password berhasil diubah");
      setCur(""); setNw(""); setConf("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingPw(false); }
  };

  const exportData = () => exportBackup(data, pid);

  // Partner invite / link
  const [partnerData, setPartnerData] = useState(null);
  const [partnerBusy, setPartnerBusy] = useState(false);

  const loadPartner = async () => {
    try {
      const { data } = await api.get("/partner/status");
      setPartnerData(data);
    } catch { setPartnerData(null); }
  };
  useEffect(() => {
    if (user && user.role !== "sysadmin" && user.role !== "Admin") loadPartner();
  }, [user?.id, user?.role]);

  const inviteUrl = partnerData?.invite_token ? `${window.location.origin}/undang-pasangan/${partnerData.invite_token}` : "";

  const genInvite = async () => {
    setPartnerBusy(true);
    try {
      await api.post("/partner/invite");
      await loadPartner();
      toast.success("Tautan undangan pasangan dibuat");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat tautan"); }
    finally { setPartnerBusy(false); }
  };
  const copyInvite = async () => {
    if (!inviteUrl) return;
    try { await navigator.clipboard.writeText(inviteUrl); toast.success("Tautan disalin"); }
    catch { toast.info(inviteUrl); }
  };
  const unlinkPartner = async () => {
    setPartnerBusy(true);
    try {
      await api.post("/partner/unlink");
      await loadPartner();
      toast.success("Pasangan berhasil di-unlink. Akun pasangan kehilangan akses paket.");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal unlink"); }
    finally { setPartnerBusy(false); }
  };

  const saveNamaPasangan = async () => {
    if (!pid) { toast.error("Pilih pasangan dulu"); return; }
    const val = namaPasangan.trim();
    if (!val) { toast.error("Nama pasangan wajib diisi"); return; }
    setSavingNama(true);
    try {
      await api.put("/pasangan-info", { nama: val }, { params: { pasangan_id: pid } });
      await reloadAll();
      toast.success("Nama pasangan diperbarui");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingNama(false); }
  };

  const saveUsername = async () => {
    const val = uname.trim();
    if (val.length < 2) { toast.error("Username minimal 2 karakter"); return; }
    if (val === user?.username) return;
    setSavingUname(true);
    try {
      await api.put("/auth/profile", { username: val });
      await refreshUser();
      toast.success("Username diperbarui");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingUname(false); }
  };

  const requestEmailCode = async () => {
    if (!emailNew.trim() || !emailPw.trim()) { toast.error("Isi email baru & password saat ini"); return; }
    setEmailBusy(true);
    try {
      await api.post("/auth/email/request-change", { new_email: emailNew.trim(), current_password: emailPw });
      setEmailStep("code");
      toast.success(`Kode verifikasi dikirim ke ${emailNew.trim()}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEmailBusy(false); }
  };

  const verifyEmailCode = async () => {
    if (!emailCode.trim()) { toast.error("Masukkan kode verifikasi"); return; }
    setEmailBusy(true);
    try {
      await api.post("/auth/email/verify-change", { code: emailCode.trim() });
      await refreshUser();
      setEmailStep("idle"); setEmailNew(""); setEmailPw(""); setEmailCode("");
      toast.success("Email berhasil diubah");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setEmailBusy(false); }
  };

  const cancelEmailChange = () => { setEmailStep("idle"); setEmailNew(""); setEmailPw(""); setEmailCode(""); };

  const shareInvite = async () => {
    if (!pid) { toast.error("Pilih pasangan dulu (khusus sysadmin di Home)"); return; }
    const link = `${window.location.origin}/u/${pid}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link undangan disalin!");
    } catch {
      toast.info(link);
    }
  };

  return (
    <div>
      <PageTitle title="Profile" subtitle="Kelola akun, foto, password, & karakter hewanmu" />

      <Card className="mb-6 p-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-4xl">
              {avatar ? <img src={avatar} alt="avatar" className="h-full w-full object-cover" /> : getAnimal(user?.animal).emoji}
            </div>
            <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md hover:-translate-y-0.5 transition-transform">
              <Camera className="h-4 w-4" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="avatar-input" />
            {avatar && (
              <button data-testid="avatar-delete-btn" onClick={removeAvatar} disabled={uploading} title="Hapus foto"
                className="absolute -top-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white shadow-md transition-transform hover:-translate-y-0.5">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1">
            <p className="font-heading text-2xl">{user?.username}</p>
            <p className="text-sm text-muted-foreground">{user?.email} · {user?.role}{user?.pasangan_id ? ` · ${user.pasangan_id}` : ""}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="export-data-btn" onClick={exportData} variant="outline" className="gap-2 rounded-full">
              <Download className="h-4 w-4" /> Backup Data
            </Button>
            <Button data-testid="share-invite-btn" onClick={shareInvite} variant="outline" className="gap-2 rounded-full">
              <Share2 className="h-4 w-4" /> Bagikan Undangan
            </Button>
          </div>
        </div>
        {uploading && <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><Upload className="h-3.5 w-3.5" /> Mengunggah foto...</p>}
      </Card>

      {user && user.role !== "sysadmin" && user.role !== "Admin" && (
        <Card className="mb-6 p-6" data-testid="plan-info-card">
          <p className="mb-1 flex items-center gap-2 font-heading text-xl">
            <Crown className="h-5 w-5 text-amber-500" /> Paket Saya
          </p>
          <p className="mb-4 text-sm text-muted-foreground">Paket aktif Anda menentukan fitur & template yang dapat diakses.</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Nama Paket</p>
              <p className="mt-1 text-lg font-semibold" data-testid="plan-name">{PLAN_LABELS[user.plan] || "Belum Ada"}</p>
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p>
              <p className={`mt-1 text-lg font-semibold capitalize ${user.plan_status === "active" ? "text-emerald-500" : user.plan_status === "expired" ? "text-red-500" : "text-amber-500"}`} data-testid="plan-status">
                {user.plan_status === "active" ? "Aktif" : user.plan_status === "expired" ? "Berakhir" : user.plan_status === "pending" ? "Menunggu" : "Belum Aktif"}
              </p>
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Sisa Waktu</p>
              <p className="mt-1 flex items-center gap-1.5 text-lg font-semibold" data-testid="plan-days-left">
                <Clock className="h-4 w-4" />
                {user.plan === "premium" && user.plan_status === "active" ? "Selamanya" :
                 user.plan_days_left != null ? `${user.plan_days_left} hari` : "-"}
              </p>
            </div>
          </div>
          {user.plan_expires_at && user.plan !== "premium" && (
            <p className="mt-3 text-xs text-muted-foreground">Berakhir: {user.plan_expires_at.slice(0, 10)}</p>
          )}
          {(user.plan_status !== "active" || (user.plan_days_left != null && user.plan_days_left <= 7)) && (
            <div className="mt-4">
              <Button data-testid="renew-plan-btn" onClick={() => window.location.href = "/paket"} className="rounded-full gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" /> {user.plan_status === "active" ? "Perpanjang Paket" : "Pilih / Perpanjang Paket"}
              </Button>
            </div>
          )}
        </Card>
      )}

      {user && user.role !== "sysadmin" && user.role !== "Admin" && (
        <Card className="mb-6 p-6" data-testid="partner-card">
          <p className="mb-1 flex items-center gap-2 font-heading text-xl">
            <Users2 className="h-5 w-5" /> Pasangan
          </p>
          <p className="mb-4 text-sm text-muted-foreground">
            {partnerData?.linked ? "Akun Anda sudah terhubung dengan pasangan. Paket berlaku bersama." : "Undang pasangan Anda untuk berbagi paket & mengelola pernikahan bersama."}
          </p>

          {partnerData?.linked && partnerData.partner ? (
            <div className="rounded-xl border border-border bg-muted/30 p-4" data-testid="linked-partner-info">
              <div className="flex items-center gap-4">
                {partnerData.partner.avatar ? (
                  <img src={partnerData.partner.avatar} alt={partnerData.partner.username} className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-2xl">
                    {getAnimal(partnerData.partner.animal)?.emoji || "🐾"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold" data-testid="partner-username">{partnerData.partner.username}</p>
                  <p className="truncate text-xs text-muted-foreground" data-testid="partner-email">{partnerData.partner.email}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Gender: {partnerData.partner.role}</p>
                </div>
                <ConfirmDelete
                  onConfirm={unlinkPartner}
                  title="Unlink pasangan?"
                  desc="Setelah unlink, akun pasangan Anda akan kehilangan akses paket bersama dan harus memilih paket sendiri."
                  trigger={
                    <Button data-testid="unlink-partner-btn" disabled={partnerBusy} variant="outline" size="sm" className="gap-1.5 rounded-full text-red-500 hover:text-red-600">
                      <Unlink className="h-3.5 w-3.5" /> Unlink
                    </Button>
                  }
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {partnerData?.invite_token ? (
                <div data-testid="partner-invite-link">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tautan Undangan (Sekali Pakai)</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input readOnly value={inviteUrl} className="font-mono text-xs" />
                    <Button data-testid="copy-invite-btn" onClick={copyInvite} variant="outline" size="icon"><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Bagikan tautan ini ke pasangan Anda. Pasangan Anda akan otomatis mendapat paket yang sama.</p>
                  <Button data-testid="regen-invite-btn" onClick={genInvite} disabled={partnerBusy} variant="ghost" size="sm" className="mt-2 gap-1.5">
                    <Link2 className="h-3.5 w-3.5" /> Buat Ulang Tautan
                  </Button>
                </div>
              ) : (
                <Button data-testid="invite-partner-btn" onClick={genInvite} disabled={partnerBusy} className="gap-1.5 rounded-full">
                  <Link2 className="h-3.5 w-3.5" /> {partnerBusy ? "Membuat..." : "Buat Tautan Undang Pasangan"}
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {isAdmin && pid && (
        <Card className="mb-6 p-6" data-testid="pasangan-info-card">
          <p className="mb-1 font-heading text-xl">Informasi Pasangan (Sysadmin)</p>
          <p className="mb-4 text-sm text-muted-foreground">Sysadmin dapat mengubah nama pasangan untuk pasangan yang sedang dipilih.</p>
          <div className="grid max-w-xl gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kode Pasangan</Label>
              <Input data-testid="kode-pasangan" value={pid} readOnly disabled className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nama Pasangan</Label>
              <Input data-testid="nama-pasangan-input" value={namaPasangan} onChange={(e) => setNamaPasangan(e.target.value)} placeholder="mis. Koala & Hamster" className="mt-1.5" />
            </div>
          </div>
          <Button data-testid="save-nama-pasangan" onClick={saveNamaPasangan} disabled={savingNama || !namaPasangan.trim() || namaPasangan === (pasanganInfo?.nama || "")} className="mt-4">
            {savingNama ? "Menyimpan..." : "Simpan Nama Pasangan"}
          </Button>
        </Card>
      )}

      <Card className="mb-6 p-6" data-testid="account-info-card">
        <p className="mb-1 font-heading text-xl">Akun (Username &amp; Email)</p>
        <p className="mb-4 text-sm text-muted-foreground">Ubah username Anda kapan saja. Untuk mengubah email, kami akan mengirim kode verifikasi 6 digit ke email baru.</p>
        <div className="grid max-w-xl gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Username</Label>
            <div className="mt-1.5 flex gap-2">
              <Input data-testid="username-input" value={uname} onChange={(e) => setUname(e.target.value)} placeholder="mis. budi" />
              <Button data-testid="save-username" onClick={saveUsername} disabled={savingUname || uname.trim() === (user?.username || "") || uname.trim().length < 2}>
                {savingUname ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input data-testid="email-current" value={user?.email || ""} readOnly disabled className="mt-1.5" />
            {emailStep === "idle" ? (
              <div className="mt-3 grid gap-3 rounded-xl border border-dashed border-border p-4">
                <p className="text-sm font-semibold">Ubah email</p>
                <Input data-testid="email-new" value={emailNew} onChange={(e) => setEmailNew(e.target.value)} placeholder="Email baru" />
                <div className="relative">
                  <Input data-testid="email-current-password" type={show ? "text" : "password"} value={emailPw} onChange={(e) => setEmailPw(e.target.value)} placeholder="Password saat ini" className="pr-10" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button data-testid="request-email-code" onClick={requestEmailCode} disabled={emailBusy || !emailNew.trim() || !emailPw.trim()} className="w-fit">
                  {emailBusy ? "Mengirim..." : "Kirim Kode Verifikasi"}
                </Button>
              </div>
            ) : (
              <div className="mt-3 grid gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
                <p className="text-sm">Kode verifikasi 6 digit dikirim ke <b>{emailNew}</b>. Berlaku 15 menit.</p>
                <Input data-testid="email-verify-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" maxLength={6} placeholder="Kode 6 digit" className="text-center text-2xl tracking-[0.5em] font-mono" />
                <div className="flex gap-2">
                  <Button data-testid="verify-email-code" onClick={verifyEmailCode} disabled={emailBusy || emailCode.length !== 6}>
                    {emailBusy ? "Memverifikasi..." : "Verifikasi & Simpan"}
                  </Button>
                  <Button data-testid="cancel-email-change" onClick={cancelEmailChange} variant="outline">Batal</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {!isAdmin && (
        <Card className="mb-6 p-6">
          <p className="mb-1 font-heading text-xl">Karakter Hewan (Tema)</p>
          <p className="mb-4 text-sm text-muted-foreground">Pilih hewan favoritmu — tema langsung dipratinjau. Klik Simpan untuk menyimpan.</p>
          <div className="max-h-72 space-y-4 overflow-y-auto pr-2">
            {ANIMAL_CATS.map((cat) => (
              <div key={cat}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{cat}</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                  {ANIMALS.filter((a) => a.cat === cat).map((a) => (
                    <button key={a.key} data-testid={`animal-${a.key}`} onClick={() => pickAnimal(a.key)}
                      className={`flex flex-col items-center gap-1 rounded-xl border p-3 transition-transform hover:-translate-y-0.5 ${animal === a.key ? "border-primary bg-primary/10" : "border-border"}`}
                      style={animal === a.key ? { borderColor: a.c } : {}}>
                      <span className="text-2xl">{a.emoji}</span>
                      <span className="text-[10px] leading-tight text-muted-foreground">{a.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="save-animal" onClick={saveAnimal} disabled={savingAnimal || animal === user?.animal} className="mt-4">
            {savingAnimal ? "Menyimpan..." : "Simpan Karakter"}
          </Button>
        </Card>
      )}

      <Card className="p-6">
        <p className="mb-4 font-heading text-xl">Ganti Password</p>
        <div className="grid max-w-md gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password Saat Ini</Label>
            <div className="relative mt-1.5">
              <Input data-testid="cur-password" type={show ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} className="pr-10" />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Password Baru</Label>
            <Input data-testid="new-password" type={show ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Konfirmasi Password Baru</Label>
            <Input data-testid="confirm-password" type={show ? "text" : "password"} value={conf} onChange={(e) => setConf(e.target.value)} className="mt-1.5" />
          </div>
          <Button data-testid="save-password" onClick={savePw} disabled={savingPw} className="w-fit">
            {savingPw ? "Menyimpan..." : "Ubah Password"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
